import { SetupFeeValidation } from "utils/validators/SetupFeeValidation";
import * as yup from "yup";

import type { Currency } from "@krea/shared/types/common";

import { BidTypeEnum } from "@/features/common/enums";
import type { IFactoringBidInput } from "@/features/lender-responses/types/lender-response";

export const factoringBidValidationSchema: (
  estimatedInvoiceAmount: number,
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) => yup.ObjectSchema<IFactoringBidInput> = (
  estimatedInvoiceAmount,
  _currency,
  formatWithCurrency,
) =>
  yup.object().shape({
    type: yup
      .string()
      .oneOf([BidTypeEnum.FACTORING])
      .required("Type is required")
      .default(BidTypeEnum.FACTORING),
    purchaseLimit: yup
      .number()
      .typeError("Purchase limit is required")
      .positive("Should be a positive number")
      .required("Purchase limit is required")
      .when("purchasedAmountPercent", ([purchasedAmountPercent], schema) =>
        purchasedAmountPercent
          ? schema.min(
              (estimatedInvoiceAmount * purchasedAmountPercent) / 100,
              (schema) =>
                `Purchase limit can't be lower than purchased invoice amount (${formatWithCurrency(schema.min)})`,
            )
          : schema,
      ),
    purchasedAmountPercent: yup
      .number()
      .typeError("Purchased percent is required")
      .required("Purchased percent is required")
      .positive("Should be a positive number")
      .max(100, `Purchased percent can't be greater than 100%`),
    commissionPercent: yup
      .number()
      .typeError("Commission is required")
      .required("Commission is required")
      .positive("Should be a positive number")
      .max(100, `Commission can't be greater than 100%`),
    paymentTermsDays: yup
      .number()
      .typeError("Payment term days is required")
      .integer("Should be an integer")
      .positive("Should be a positive number")
      .required("Payment term days is required"),
    withRegress: yup.boolean().required("Required"),
    regressNumDaysAfterDueDate: yup
      .number()
      .integer("Should be an integer")
      .min(0, `Regress number of days after due date can't be lower than 0`)
      .nullable()
      .when("withRegress", {
        is: true,
        then: (schema) => {
          return schema.required(
            "Regress number of days after due date is required",
          );
        },
      }),
    setupFee: SetupFeeValidation(formatWithCurrency, estimatedInvoiceAmount),
    monthlyFee: yup
      .number()
      .nullable()
      .min(0, `Monthly fee can't be lower than 0`),
    handlingFeePerInvoice: yup
      .number()
      .nullable()
      .positive("Should be a positive number")
      .min(0, `Handling fee can't be lower than 0`),
    limitFeePerYearPercent: yup
      .number()
      .nullable()
      .min(0, `Limit fee per year can't be lower than 0`)
      .max(100, `Limit fee per year can't be greater than 100%`),
  });

export const invoicePurchasedBidDefaultValues: IFactoringBidInput = {
  type: BidTypeEnum.FACTORING,
  purchaseLimit: undefined,
  purchasedAmountPercent: undefined,
  commissionPercent: undefined,
  paymentTermsDays: undefined,
  withRegress: undefined,
  regressNumDaysAfterDueDate: undefined,
  setupFee: undefined,
  limitFeePerYearPercent: undefined,
  handlingFeePerInvoice: undefined,
  monthlyFee: undefined,
};
